import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Search, ArrowUpDown, ArrowUp, ArrowDown, X } from 'lucide-react';

interface ProfileRow {
  user_id: string;
  full_name: string | null;
  email: string | null;
  school_id: string | null;
  created_at: string;
}

interface RoleRow {
  user_id: string;
  role: string;
}

interface SchoolRow {
  id: string;
  name: string;
  plan_tier: string | null;
  billing_status: string | null;
}

type SortField = 'full_name' | 'email' | 'created_at';
type SortDir = 'asc' | 'desc';

const ROLE_OPTIONS = [
  { value: '__all__', label: 'All roles' },
  { value: 'school_admin', label: 'School admin' },
  { value: 'slt', label: 'SLT' },
  { value: 'teacher', label: 'Teacher' },
  { value: 'individual', label: 'Individual' },
  { value: 'super_admin', label: 'Super admin' },
];

const PLAN_OPTIONS = [
  { value: '__all__', label: 'All plans' },
  { value: 'free', label: 'Free' },
  { value: 'individual', label: 'Individual' },
  { value: 'school', label: 'School' },
  { value: 'trust', label: 'Trust' },
];

const STATUS_OPTIONS = [
  { value: '__all__', label: 'All statuses' },
  { value: 'active', label: 'Active' },
  { value: 'trialing', label: 'Trialing' },
  { value: 'past_due', label: 'Past due' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'paused', label: 'Paused' },
];

function SortIcon({ field, sortField, sortDir }: { field: SortField; sortField: SortField; sortDir: SortDir }) {
  if (field !== sortField) return <ArrowUpDown className="ml-1 h-3.5 w-3.5 opacity-40 inline" />;
  return sortDir === 'asc'
    ? <ArrowUp className="ml-1 h-3.5 w-3.5 inline" />
    : <ArrowDown className="ml-1 h-3.5 w-3.5 inline" />;
}

export default function AdminUsers() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('__all__');
  const [schoolFilter, setSchoolFilter] = useState('__all__');
  const [planFilter, setPlanFilter] = useState('__all__');
  const [statusFilter, setStatusFilter] = useState('__all__');
  const [noSchoolOnly, setNoSchoolOnly] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo,   setDateTo]   = useState('');
  const [sortField, setSortField] = useState<SortField>('created_at');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const { data: profiles = [], isLoading: profilesLoading } = useQuery({
    queryKey: ['admin-all-profiles'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('user_id, full_name, email, school_id, created_at')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data as ProfileRow[];
    },
  });

  const { data: roles = [] } = useQuery({
    queryKey: ['admin-all-roles'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('user_roles')
        .select('user_id, role');
      if (error) throw error;
      return data as RoleRow[];
    },
  });

  const { data: schools = [] } = useQuery({
    queryKey: ['admin-schools-list'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('schools')
        .select('id, name, plan_tier, billing_status')
        .order('name');
      if (error) throw error;
      return data as SchoolRow[];
    },
  });

  const rolesByUser = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const r of roles) {
      const list = map.get(r.user_id) ?? [];
      list.push(r.role);
      map.set(r.user_id, list);
    }
    return map;
  }, [roles]);

  const schoolById = useMemo(() => {
    const map = new Map<string, SchoolRow>();
    for (const s of schools) map.set(s.id, s);
    return map;
  }, [schools]);

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    let result = profiles.filter(p => {
      if (q && !(p.full_name?.toLowerCase().includes(q) || p.email?.toLowerCase().includes(q))) return false;

      if (noSchoolOnly && p.school_id) return false;

      if (roleFilter !== '__all__') {
        const userRoles = rolesByUser.get(p.user_id) ?? [];
        if (!userRoles.includes(roleFilter)) return false;
      }

      if (schoolFilter !== '__all__' && p.school_id !== schoolFilter) return false;

      const school = p.school_id ? schoolById.get(p.school_id) : undefined;

      if (planFilter !== '__all__' && school?.plan_tier !== planFilter) return false;

      if (statusFilter !== '__all__' && school?.billing_status !== statusFilter) return false;
      if (dateFrom && p.created_at < dateFrom) return false;
      if (dateTo   && p.created_at.substring(0, 10) > dateTo) return false;

      return true;
    });

    result = [...result].sort((a, b) => {
      const av = (a[sortField] ?? '').toLowerCase();
      const bv = (b[sortField] ?? '').toLowerCase();
      const cmp = av.localeCompare(bv);
      return sortDir === 'asc' ? cmp : -cmp;
    });

    return result;
  }, [profiles, search, roleFilter, schoolFilter, planFilter, statusFilter, noSchoolOnly, dateFrom, dateTo, sortField, sortDir, rolesByUser, schoolById]);

  const hasActiveFilters = search || roleFilter !== '__all__' || schoolFilter !== '__all__' ||
    planFilter !== '__all__' || statusFilter !== '__all__' || noSchoolOnly || !!dateFrom || !!dateTo;

  const clearFilters = () => {
    setSearch('');
    setRoleFilter('__all__');
    setSchoolFilter('__all__');
    setPlanFilter('__all__');
    setStatusFilter('__all__');
    setNoSchoolOnly(false);
    setDateFrom('');
    setDateTo('');
  };

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

  const thProps = (field: SortField) => ({
    className: 'cursor-pointer select-none whitespace-nowrap px-4 py-3 font-medium text-left',
    onClick: () => toggleSort(field),
  });

  return (
    <DashboardLayout>
      <div className="p-6 max-w-6xl mx-auto space-y-4">
        <div>
          <h1 className="text-2xl font-bold">Users</h1>
          <p className="text-sm text-muted-foreground mt-1">All registered platform users</p>
        </div>

        {/* Filter bar — first row */}
        <div className="flex flex-wrap gap-2 items-center">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by name or email…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9 w-60"
            />
          </div>

          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ROLE_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>

          <Select value={schoolFilter} onValueChange={v => { setSchoolFilter(v); }}>
            <SelectTrigger className="w-44">
              <SelectValue placeholder="All schools" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All schools</SelectItem>
              {schools.map(s => (
                <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={planFilter} onValueChange={setPlanFilter}>
            <SelectTrigger className="w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PLAN_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>

          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>

          <Button
            variant={noSchoolOnly ? 'default' : 'outline'}
            size="sm"
            onClick={() => setNoSchoolOnly(v => !v)}
            className="text-sm"
          >
            No school
          </Button>

          <div className="flex items-center gap-1.5">
            <span className="text-xs text-muted-foreground shrink-0">Joined</span>
            <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="h-8 w-36 text-sm" />
            <span className="text-xs text-muted-foreground">–</span>
            <Input type="date" value={dateTo}   onChange={e => setDateTo(e.target.value)}   className="h-8 w-36 text-sm" />
          </div>

          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters} className="text-muted-foreground">
              <X className="h-3.5 w-3.5 mr-1" />Clear
            </Button>
          )}
        </div>

        {/* Table */}
        {profilesLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : (
          <div className="rounded-lg border overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th {...thProps('full_name')}>
                    Name <SortIcon field="full_name" sortField={sortField} sortDir={sortDir} />
                  </th>
                  <th {...thProps('email')}>
                    Email <SortIcon field="email" sortField={sortField} sortDir={sortDir} />
                  </th>
                  <th className="text-left px-4 py-3 font-medium">School</th>
                  <th className="text-left px-4 py-3 font-medium">Role</th>
                  <th className="text-left px-4 py-3 font-medium">Plan</th>
                  <th className="text-left px-4 py-3 font-medium">Billing</th>
                  <th {...thProps('created_at')}>
                    Joined <SortIcon field="created_at" sortField={sortField} sortDir={sortDir} />
                  </th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">
                      No users match the current filters.
                    </td>
                  </tr>
                ) : (
                  filtered.map(p => {
                    const school = p.school_id ? schoolById.get(p.school_id) : undefined;
                    const userRoles = rolesByUser.get(p.user_id) ?? [];
                    return (
                      <tr
                        key={p.user_id}
                        className="border-b last:border-0 hover:bg-muted/30 cursor-pointer"
                        onClick={() => navigate(`/admin/users/${p.user_id}`)}
                      >
                        <td className="px-4 py-3 font-medium">{p.full_name ?? '—'}</td>
                        <td className="px-4 py-3 text-muted-foreground">{p.email ?? '—'}</td>
                        <td className="px-4 py-3">
                          {school?.name ?? <span className="text-muted-foreground">—</span>}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1">
                            {userRoles.length === 0
                              ? <span className="text-muted-foreground">—</span>
                              : userRoles.map(r => (
                                <Badge key={r} variant="secondary" className="text-xs">{r.replace(/_/g, ' ')}</Badge>
                              ))}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {school?.plan_tier
                            ? <Badge variant="outline" className="text-xs">{school.plan_tier}</Badge>
                            : <span className="text-muted-foreground">—</span>}
                        </td>
                        <td className="px-4 py-3">
                          {school?.billing_status
                            ? <Badge variant={school.billing_status === 'active' ? 'default' : school.billing_status === 'past_due' || school.billing_status === 'cancelled' ? 'destructive' : 'secondary'} className="text-xs">
                                {school.billing_status.replace('_', ' ')}
                              </Badge>
                            : <span className="text-muted-foreground">—</span>}
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">{formatDate(p.created_at)}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
        <p className="text-xs text-muted-foreground">
          {hasActiveFilters
            ? `${filtered.length} of ${profiles.length} user${profiles.length !== 1 ? 's' : ''}`
            : `${profiles.length} user${profiles.length !== 1 ? 's' : ''}`}
        </p>
      </div>
    </DashboardLayout>
  );
}
